/** @jsxImportSource solid-js */

import {
  Component,
  Show,
  For,
  createSignal,
  createEffect,
  createMemo,
  onMount,
  batch,
  on,
} from 'solid-js';
import { createStore } from 'solid-js/store';
import { ErrorBoundary } from 'solid-js';
import {
  IAttachedFileList,
  ITab,
  IAttachmentPickerProps,
  IVisibleTab,
} from './types';
import FilesFromComputerContainer from './FilesFromComputer';
import FilesFromConnectContainer from './FilesFromConnect';
import AttachAsUrlContainer from './AttachAsUrl';
import ViewListContainer from './ViewList';
import { defineSolidPopup } from '@tcweb/connect-ui';
import { translate } from '@fulcrum/lib';
import {
  ProjectRegionProvider,
  TCPSServiceProvider,
} from '@tc-attachment-picker/providers';

const AttachmentPickerWithProviders: Component<IAttachmentPickerProps> = (
  props
) => {
  return (
    <ProjectRegionProvider>
      <TCPSServiceProvider>
        <AttachmentPickerInternal {...props} />
      </TCPSServiceProvider>
    </ProjectRegionProvider>
  );
};

const AttachmentPickerInternal: Component<IAttachmentPickerProps> = (props) => {
  const [tab, setTab] = createSignal<ITab>(ITab.FileFromConnect);
  const [isShowFileFromConnect, setIsShowFileFromConnect] = createSignal(false);
  const [isShowView, setIsShowView] = createSignal(false);
  const [isShowLink, setIsShowLink] = createSignal(false);
  const [isShowFileFromComputer, setIsShowFileFromComputer] =
    createSignal(false);
  const [isShow2DView, setIsShow2DView] = createSignal(false);
  const [isShow3DView, setIsShow3DView] = createSignal(false);
  const [filesList, setFilesList] = createStore<IAttachedFileList>({
    selectedObject: [],
  });
  const [attachUrlSubmitRef, setAttachUrlSubmitRef] =
    createSignal<HTMLButtonElement>();

  onMount(() => {
    defineSolidPopup();
    createEffect(() => {
      setAttachmentVisibility();
      if (isShowFileFromConnect()) {
        setTab(ITab.FileFromConnect);
      } else if (isShow2DView() || isShow3DView()) {
        setTab(ITab.View);
      } else if (isShowFileFromComputer()) {
        setTab(ITab.FileFromComputer);
      } else {
        setTab(ITab.Link);
      }
    });
  });

  // Reset filesList when tab changes - optimized to prevent unnecessary re-renders
  createEffect(
    on(
      tab,
      (currentTab, prevTab) => {
        if (prevTab !== undefined && prevTab !== currentTab) {
          // Use batch to group updates and prevent cascading re-renders
          batch(() => {
            setFilesList({ selectedObject: [] });
          });
        }
      },
      { defer: true } // Defer execution to avoid initial trigger
    )
  );

  const setAttachmentVisibility = (): void => {
    let types = ['ALL'];
    if (props.allowedAttachmentTypes) {
      types = props.allowedAttachmentTypes.split(',');
    }

    if (types.includes('ALL')) {
      setIsShowFileFromConnect(true);
      setIsShowView(true);
      setIsShow2DView(true);
      setIsShow3DView(true);
      setIsShowFileFromComputer(true);
      setIsShowLink(true);
      setTab(ITab.FileFromConnect);
    } else {
      if (types.includes('2D_VIEW')) {
        setIsShow2DView(true);
        setIsShowView(true);
      }
      if (types.includes('3D_VIEW')) {
        setIsShow3DView(true);
        setIsShowView(true);
      }
      if (types.includes('FILE_FROM_CONNECT')) {
        setIsShowFileFromConnect(true);
      }
      if (types.includes('FILE_FROM_COMPUTER')) {
        setIsShowFileFromComputer(true);
      }
      if (types.includes('LINK')) {
        setIsShowLink(true);
      }
    }
  };

  // Create stable callback reference to prevent child component re-renders
  const onFilesSubmit = createMemo(() => (files: IAttachedFileList) => {
    // // Use batch to group updates and prevent cascading re-renders
    batch(() => {
      setFilesList(files);
    });
  });

  const handlePrimaryButtonClick = (): void => {
    if (tab() === ITab.Link) {
      const submitButton = attachUrlSubmitRef();
      if (submitButton) {
        submitButton.click();
      }
    } else if (
      Array.isArray(filesList.selectedObject) &&
      filesList.selectedObject.length > 0
    ) {
      props.addAttachment(filesList);
    }
  };

  const visibleTabs = (): IVisibleTab[] => {
    const tabs: IVisibleTab[] = [];

    if (isShowFileFromConnect()) {
      tabs.push({ type: ITab.FileFromConnect, tabIcon: 'tc-icon-explorer' });
    }
    if (isShow2DView() || isShow3DView()) {
      tabs.push({ type: ITab.View, tabIcon: 'tc-icon-views' });
    }
    if (isShowFileFromComputer()) {
      tabs.push({
        type: ITab.FileFromComputer,
        tabIcon: 'tc-icon-laptop d-none d-md-block',
      });
    }
    if (isShowLink()) {
      tabs.push({ type: ITab.Link, tabIcon: 'tc-icon-link' });
    }

    return tabs;
  };

  // Create memoized computed values to prevent unnecessary re-calculations
  const isPrimaryButtonDisabled = createMemo(() => {
    if (tab() === ITab.Link) return false;
    return (
      props.inProgressFlag ||
      (Array.isArray(filesList.selectedObject) &&
        filesList.selectedObject.length === 0)
    );
  });

  const renderContent = createMemo(() => (
    <div>
      <ul class="tabs mb-1">
        <For each={visibleTabs()}>
          {(tabInfo) => (
            <li
              class={`tabs-title ${tab() === tabInfo.type ? 'active' : ''}`}
              onClick={(event) => {
                event.stopPropagation();
                setTab(tabInfo.type);
              }}
              data-cy={tabInfo.type.toLowerCase()}
            >
              <i class={`icon-font ${tabInfo.tabIcon}`} />
              <Show when={tabInfo.type === ITab.FileFromComputer}>
                <i class="icon-font tc-icon-camera-alt d-block d-md-none" />
              </Show>
            </li>
          )}
        </For>
      </ul>

      <FilesFromConnectContainer
        onFilesSubmit={onFilesSubmit()}
        projectName={props.projectName || 'Project Name'}
        tab={tab()}
        isShowFileFromConnect={isShowFileFromConnect()}
        disableMultiSelect={props.disableMultiSelect}
        allowedFileExtensions={props.allowedFileExtensions}
        allowedFileNames={props.allowedFileNames}
        is3DEmbbedded={props.is3DEmbbedded}
        projectId={props.projectId}
        accessToken={props.accessToken}
        tcpsApiDomain={props.tcpsApiDomain}
      />

      <ViewListContainer
        onFilesSubmit={onFilesSubmit()}
        isShow2DView={isShow2DView()}
        isShow3DView={isShow3DView()}
        tab={tab()}
        isShowView={isShowView()}
        disableMultiSelect={props.disableMultiSelect}
      />

      <AttachAsUrlContainer
        onFilesSubmit={onFilesSubmit()}
        addRef={setAttachUrlSubmitRef}
        addAttachment={props.addAttachment}
        tab={tab()}
        isShowLink={isShowLink()}
      />

      <FilesFromComputerContainer
        onFilesSubmit={onFilesSubmit()}
        tab={tab()}
        isShowFileFromComputer={isShowFileFromComputer()}
        disableMultiSelect={props.disableMultiSelect}
        allowedUploadFileExt={props.allowedUploadFileExt}
        allowedFileExtensions={props.allowedFileExtensions}
        allowedFileNames={props.allowedFileNames}
        is3DEmbbedded={props.is3DEmbbedded}
      />
    </div>
  ));

  return (
    // @ts-expect-error - tc-popup is a web component defined at runtime
    <tc-popup
      show={true}
      heading={props.customTitle ?? translate('TC.Attachments.Add.Multiple')}
      primary-btn-label={
        props.customButtonName ?? translate('TC.Attachments.Add.Multiple')
      }
      primary-btn-class="primary"
      primary-btn-disabled={isPrimaryButtonDisabled()}
      secondary-btn-label={translate('TC.Common.Cancel')}
      secondary-btn-class="link-secondary"
      overflow-style="padding: 0 1rem 1rem 1rem;"
      content={renderContent()}
      ref={(el: HTMLElement) => {
        el.addEventListener('popup-primary-action', handlePrimaryButtonClick);
        el.addEventListener('popup-secondary-action', props.onClose);
        el.addEventListener('popup-close', props.onClose);
      }}
    />
  );
};

const AttachmentPicker: Component<IAttachmentPickerProps> = (props) => {
  return (
    <ErrorBoundary
      fallback={(error) => {
        console.error('AttachmentPicker error:', error);
        return <div class="error-message">Error loading AttachmentPicker</div>;
      }}
    >
      <AttachmentPickerWithProviders {...props} />
    </ErrorBoundary>
  );
};

export default AttachmentPicker;
